import { DAY_MS } from "./freshness.js";

// PG-64: the interval a Progress chart shows, handed in as a parameter
// instead of each chart reading the clock. Progress passes its fixed trailing
// windows; the report passes the period the athlete picked, past included.
// Both ends are inclusive epoch ms.
export type TimeRange = { fromMs: number; toMs: number };

/** The last `days` days up to `nowMs`. */
export const trailingRange = (nowMs: number, days: number): TimeRange => ({
  fromMs: nowMs - days * DAY_MS,
  toMs: nowMs,
});

export const inTimeRange = (ms: number, r: TimeRange): boolean =>
  ms >= r.fromMs && ms <= r.toMs;

/** Local midnight that opens the day of `ms`. */
export const startOfLocalDay = (ms: number): number => {
  const d = new Date(ms);
  d.setHours(0, 0, 0, 0);
  return d.getTime();
};

/** `ms` moved by `n` local calendar days (same wall-clock time), not by
 *  n × 24 h: across a DST change the two differ by an hour, which from a
 *  local midnight lands on the wrong day. */
export const addLocalDays = (ms: number, n: number): number => {
  const d = new Date(ms);
  d.setDate(d.getDate() + n);
  return d.getTime();
};

/** Whole local calendar days from the day of `thenMs` to the day of `nowMs`
 *  (Sunday 15:36 to Thursday 14:57 is 4). Rounded over the difference of the
 *  two midnights, so a 23 h or 25 h day (DST) still counts as one. */
export const calendarDaysBetween = (thenMs: number, nowMs: number): number =>
  Math.max(0, Math.round((startOfLocalDay(nowMs) - startOfLocalDay(thenMs)) / DAY_MS));

/** Local "YYYY-MM-DD", from the local y/m/d parts: never via
 *  toISOString, which shifts the day for a zone behind UTC. */
export const formatLocalYmd = (d: Date): string => {
  const p = (n: number) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`;
};

/** The last millisecond of the local day of `ms`. */
export const endOfLocalDay = (ms: number): number => {
  const d = new Date(ms);
  d.setHours(23, 59, 59, 999);
  return d.getTime();
};
